import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
// material

import {
  Box,
  Grid,
  Typography,
  Modal,
  FormControl,
  TextField,
  Autocomplete,
  Button,
  Stack,
  IconButton
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import {
  computeHierarchyMeta,
  extractHierarchyInfoFromPath,
  findOptionByNameOrId,
  getChangedFields,
  parseValidationErrorMessage
} from '../../utils/Utilities';
import { useAuth } from '../../components/customContextProviders/AuthContext';
import { AddNewBranch } from '../../network/User';
import { SnackBarAlert } from '../../components/HelperComponents';
import { APP_CONSTANTS } from '../../URLConstants';
import { UpdateBranch } from '../network/Branch';

// ----------------------------------------------------------------------

const style = {
  width: '100%',
  maxWidth: { sm: '90%', xs: '90%', md: 800 },
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: '30%',
  bgcolor: 'background.paper',
  border: '1px solid #000',
  boxShadow: 24,
  p: 3,
  borderRadius: 1
};

const BranchSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  regionalOfficeName: Yup.string().required('Required'),
  zoneOfficeName: Yup.string().required('Required'),
  headOfficeName: Yup.string().required('Required'),
  district: Yup.string().required('Required'),
  city: Yup.string().required('Required'),
  branchId: Yup.string().required('Required'),
  address: Yup.string().required('Required'),
  pincode: Yup.string().required('Required'),
  state: Yup.string().required('Required')
});

AddBranchModal.propTypes = {
  opneAssignedModal: PropTypes.bool,
  handleClose: PropTypes.func,
  zonesList: PropTypes.object,
  regionsList: PropTypes.object,
  fetchZones: PropTypes.func,
  fetchRegions: PropTypes.func,
  headOfficesList: PropTypes.object,
  onUpdate: PropTypes.func
};

export default function AddBranchModal({
  opneAssignedModal,
  handleClose,
  zonesList,
  regionsList,
  headOfficesList,
  fetchZones,
  fetchRegions,
  selectedBranch,
  onUpdate
}) {
  const branchSelectedForUpdate = selectedBranch?.branch
  const { sessionUserData, accountDetails } = useAuth();

  const zoneIdOfSelectedBranchForUpdate = regionsList?.find((item) => item.id === branchSelectedForUpdate?.regionalOfficeId)?.zoneOfficeId
  const zoneOfSelectedBranchForUpdate = zonesList?.find((item) => item.id === zoneIdOfSelectedBranchForUpdate)

  const sessionUserPath = accountDetails?.path;

  const {
    isAdminUser,
    hierarchyInfo,
    isHeadOfficeLocked,
    isZoneOfficeLocked,
    isRegionalOfficeLocked
  } = useMemo(
    () => computeHierarchyMeta(sessionUserPath, sessionUserData?.user?.role),
    [sessionUserPath, sessionUserData?.user?.role]
  );

  const [isSnackBarAlertOpen, setIsSnackBarAlertOpen] = useState(false);
  const [snackBarMetas, setSnackBarMetas] = useState({
    title: '',
    message: '',
    severity: 'info',
    handleClose: null
  });

  const AddBranchFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: branchSelectedForUpdate?.name || '',
      branchId: branchSelectedForUpdate?.branchId || '',
      address: branchSelectedForUpdate?.address || '',
      district: branchSelectedForUpdate?.district || '',
      city: branchSelectedForUpdate?.city || '',
      state: branchSelectedForUpdate?.state || '',
      pincode: branchSelectedForUpdate?.pincode || '',
      zoneOfficeName: zoneOfSelectedBranchForUpdate?.name || extractHierarchyInfoFromPath(sessionUserPath)?.zoneOffice || '',
      zoneOfficeId: zoneIdOfSelectedBranchForUpdate || '',
      regionalOfficeId: branchSelectedForUpdate?.regionalOfficeId || '',
      regionalOfficeName: branchSelectedForUpdate?.regionalOffice?.name || extractHierarchyInfoFromPath(sessionUserPath)?.regionalOffice || '',
      headOfficeId: headOfficesList?.[0]?.id || '',
      headOfficeName: headOfficesList?.[0]?.name || ''
    },
    validationSchema: BranchSchema,
    onSubmit: () => {

      const basePayload = {
        name: values?.name,
        address: values?.address,
        district: values?.district,
        city: values?.city,
        state: values?.state,
        pincode: values?.pincode,
        branchId: values?.branchId,
        regionalOfficeId: values?.regionalOfficeId
      };

      let payload;

      if (branchSelectedForUpdate?.id) {
        const changedFields = getChangedFields(basePayload, branchSelectedForUpdate);

        if (Object.keys(changedFields).length === 0) {
          AddBranchFormik.setSubmitting(false);
          onUpdate(APP_CONSTANTS?.NO_CHANGES, 'No changes detected');
          return;
        }

        payload = changedFields;
        updateBranch(branchSelectedForUpdate?.id, payload);
      } else {
        payload = Object.fromEntries(
          Object.entries(basePayload).filter(([_, v]) => v !== undefined && v !== null && v !== '')
        );
        addNewBranch(payload);
      }
    }
  });

  function addNewBranch(payload) {
    AddNewBranch(payload)
      .then((res) => {
        AddBranchFormik.setSubmitting(false);
        handleClose();
        const message = `${AddBranchFormik?.values?.name} successfully created !`;
        if (onUpdate) {
          onUpdate(APP_CONSTANTS?.BRANCH_UPDATE_SUCCESSFULL, message);
        } else {
          setSnackBarMetas((prevState) => ({
            ...prevState,
            title: message,
            severity: 'success'
          }));
          setIsSnackBarAlertOpen(true);
        }

        AddBranchFormik.resetForm();
      })
      .catch((error) => {
        AddBranchFormik.setSubmitting(false);
        setSnackBarMetas((prevState) => ({
          ...prevState,
          title: parseValidationErrorMessage(error),
          severity: 'error'
        }));
        setIsSnackBarAlertOpen(true);
      });
  }

  function updateBranch(branchId, payload) {
    UpdateBranch(branchId, payload)
      .then((res) => {
        AddBranchFormik.setSubmitting(false);
        handleClose();
        onUpdate(APP_CONSTANTS?.BRANCH_UPDATE_SUCCESSFULL, 'Branch has been updated!');
        AddBranchFormik.resetForm();
      })
      .catch((error) => {
        AddBranchFormik.setSubmitting(false);
        onUpdate(APP_CONSTANTS?.BRANCH_UPDATE_UNSUCCESSFULL, parseValidationErrorMessage(error));
      });
  }

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps, setFieldValue } =
    AddBranchFormik;

  useEffect(() => {
    if (values?.zoneOfficeId) {
      fetchRegions('', values?.zoneOfficeId)
    }
  }, [values?.zoneOfficeId])
  // Prefill hierarchy names from taxonomy path (Admin users only)
  useEffect(() => {
    if (!opneAssignedModal) return;
    if (!isAdminUser) return;

    if (hierarchyInfo?.headOffice && !values?.headOfficeName) {
      setFieldValue('headOfficeName', hierarchyInfo.headOffice);
    }

    if (hierarchyInfo?.zoneOffice && !values?.zoneOfficeName) {
      setFieldValue('zoneOfficeName', hierarchyInfo.zoneOffice);
    }

    if (hierarchyInfo?.regionalOffice && !values?.regionalOfficeName) {
      setFieldValue('regionalOfficeName', hierarchyInfo.regionalOffice);
    }
  }, [
    opneAssignedModal,
    isAdminUser,
    hierarchyInfo?.headOffice,
    hierarchyInfo?.zoneOffice,
    hierarchyInfo?.regionalOffice
  ]);

  // Map Head Office name/id -> headOfficeId and load Zones (required for HO/ZO/RO admins)
  useEffect(() => {
    if (!opneAssignedModal) return;
    if (!isHeadOfficeLocked) return;
    if (values?.headOfficeId) return;

    const desired = hierarchyInfo?.headOffice || values?.headOfficeName;
    const match = findOptionByNameOrId(headOfficesList, desired);
    if (!match) return;

    setFieldValue('headOfficeId', match.id);
    setFieldValue('headOfficeName', match.name || desired || '');

    if (match?.id) {
      fetchZones(match.id);
    }
  }, [
    opneAssignedModal,
    isHeadOfficeLocked,
    hierarchyInfo?.headOffice,
    headOfficesList,
    values?.headOfficeId,
    values?.headOfficeName
  ]);

  // Map Zone Office name/id -> zoneOfficeId and load Regions (required for ZO admin, also safe for RO admin)
  useEffect(() => {
    if (!opneAssignedModal) return;
    if (!isZoneOfficeLocked) return;
    if (values?.zoneOfficeId) return;

    const desired = hierarchyInfo?.zoneOffice || values?.zoneOfficeName;
    const match = findOptionByNameOrId(zonesList, desired);
    if (!match) return;

    setFieldValue('zoneOfficeId', match.id);
    setFieldValue('zoneOfficeName', match.name || desired || '');

    if (match?.id) {
      fetchRegions(null, match.id);
    }
  }, [
    opneAssignedModal,
    isZoneOfficeLocked,
    hierarchyInfo?.zoneOffice,
    zonesList,
    values?.zoneOfficeId,
    values?.zoneOfficeName
  ]);

  // Map Regional Office name/id -> regionalOfficeId (RO admin only)
  useEffect(() => {
    if (!opneAssignedModal) return;
    if (!isRegionalOfficeLocked) return;
    if (values?.regionalOfficeId) return;

    const desired = hierarchyInfo?.regionalOffice || values?.regionalOfficeName;
    const match = findOptionByNameOrId(regionsList, desired);
    if (!match) return;

    setFieldValue('regionalOfficeId', match.id);
    setFieldValue('regionalOfficeName', match.name || desired || '');
  }, [
    opneAssignedModal,
    isRegionalOfficeLocked,
    hierarchyInfo?.regionalOffice,
    regionsList,
    values?.regionalOfficeId,
    values?.regionalOfficeName
  ]);

  return (
    <Box>
      <Modal
        open={opneAssignedModal}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <FormikProvider enableReinitialize="true" value={AddBranchFormik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Box sx={style}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'Montserrat' }}>
                  {branchSelectedForUpdate?.id ? 'Update Branch' : 'Create New Branch'}
                </Typography>
                <IconButton
                  onClick={() => {
                    handleClose();
                    AddBranchFormik?.resetForm();
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>

              <Grid container spacing={2} sx={{ my: 1 }}>
                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="Branch Name *"
                      id="branchName"
                      name="branchName"
                      autoComplete="off"
                      variant="outlined"
                      {...getFieldProps('name')}
                      error={Boolean(touched?.name && errors?.name)}
                      helperText={touched?.name && errors?.name}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="Branch Id *"
                      id="branchId"
                      name="branchId"
                      autoComplete="off"
                      variant="outlined"
                      {...getFieldProps('branchId')}
                      error={Boolean(touched?.branchId && errors?.branchId)}
                      helperText={touched?.branchId && errors?.branchId}
                      disabled={branchSelectedForUpdate?.id}
                    />
                  </FormControl>
                </Grid>

                {/* Head Office */}
                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                  {isHeadOfficeLocked ? (
                    <TextField
                      label="Head Office *"
                      value={values?.headOfficeName || ''}
                      disabled
                      error={Boolean(touched?.headOfficeName && errors?.headOfficeName)}
                      helperText={touched?.headOfficeName && errors?.headOfficeName}
                      inputProps={{ 'aria-label': 'Head office' }}
                      fullWidth
                    />
                  ) : (
                    <Autocomplete
                      options={headOfficesList || []}
                      getOptionLabel={(option) => option.name || ''}
                      value={headOfficesList.find((o) => o.id === values.headOfficeId) || null}
                      onChange={(e, newValue) => {
                        const selectedId = newValue ? newValue.id : '';
                        setFieldValue('headOfficeId', selectedId);
                        setFieldValue('headOfficeName', newValue ? newValue.name : '');
                        setFieldValue('zoneOfficeId', '');
                        setFieldValue('zoneOfficeName', '');
                        setFieldValue('regionalOfficeId', '');
                        setFieldValue('regionalOfficeName', '');
                        if (selectedId) {
                          fetchZones(selectedId);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Head Office *"
                          error={Boolean(touched?.headOfficeId && errors?.headOfficeId)}
                          helperText={touched?.headOfficeId && errors?.headOfficeId}
                          inputProps={{
                            ...params.inputProps,
                            'aria-label': 'Select Head office'
                          }}
                        />
                      )}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                    />
                  )}
                </Grid>

                {/* Zone Office */}
                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                  {isZoneOfficeLocked ? (
                    <TextField
                      label="Zone Office *"
                      value={values?.zoneOfficeName || ''}
                      disabled
                      error={Boolean(touched?.zoneOfficeName && errors?.zoneOfficeName)}
                      helperText={touched?.zoneOfficeName && errors?.zoneOfficeName}
                      inputProps={{ 'aria-label': 'Zone office' }}
                      fullWidth
                    />
                  ) : (
                    <Autocomplete
                      options={zonesList || []}
                      getOptionLabel={(option) => option.name || ''}
                      value={zonesList.find((z) => z.id === values.zoneOfficeId) || null}
                      onChange={(e, newValue) => {
                        const selectedId = newValue ? newValue.id : '';
                        setFieldValue('zoneOfficeId', selectedId);
                        setFieldValue('zoneOfficeName', newValue ? newValue.name : '');
                        setFieldValue('regionalOfficeId', '');
                        setFieldValue('regionalOfficeName', '');
                        if (selectedId) {
                          fetchRegions(null, selectedId);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Zone Office *"
                          error={Boolean(touched?.zoneOfficeId && errors?.zoneOfficeId)}
                          helperText={touched?.zoneOfficeId && errors?.zoneOfficeId}
                          inputProps={{
                            ...params.inputProps,
                            'aria-label': 'Select Zone office'
                          }}
                        />
                      )}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      disabled={!values?.headOfficeName}
                    />
                  )}
                </Grid>

                {/* Regional Office */}
                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                  {isRegionalOfficeLocked ? (
                    <TextField
                      label="Regional Office *"
                      value={values?.regionalOfficeName || ''}
                      disabled
                      error={Boolean(touched?.regionalOfficeName && errors?.regionalOfficeName)}
                      helperText={touched?.regionalOfficeName && errors?.regionalOfficeName}
                      inputProps={{ 'aria-label': 'Regional office' }}
                      fullWidth
                    />
                  ) : (
                    <Autocomplete
                      options={regionsList || []}
                      getOptionLabel={(option) => option.name || ''}
                      value={regionsList.find((r) => r.id === values.regionalOfficeId) || null}
                      onChange={(e, newValue) => {
                        const selectedId = newValue ? newValue.id : '';
                        setFieldValue('regionalOfficeId', selectedId);
                        setFieldValue('regionalOfficeName', newValue ? newValue.name : '');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Regional Office *"
                          error={Boolean(touched?.regionalOfficeId && errors?.regionalOfficeId)}
                          helperText={touched?.regionalOfficeId && errors?.regionalOfficeId}
                          inputProps={{
                            ...params.inputProps,
                            'aria-label': 'Select Regional office'
                          }}
                        />
                      )}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      disabled={!values?.zoneOfficeId}
                    />
                  )}
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="Pincode *"
                      id="pincode"
                      name="pincode"
                      autoComplete="off"
                      variant="outlined"
                      {...getFieldProps('pincode')}
                      error={Boolean(touched?.pincode && errors?.pincode)}
                      helperText={touched?.pincode && errors?.pincode}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="District *"
                      id="district"
                      name="district"
                      autoComplete="off"
                      variant="outlined"
                      {...getFieldProps('district')}
                      error={Boolean(touched?.district && errors?.district)}
                      helperText={touched?.district && errors?.district}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="City *"
                      id="city"
                      name="city"
                      autoComplete="off"
                      variant="outlined"
                      {...getFieldProps('city')}
                      error={Boolean(touched?.city && errors?.city)}
                      helperText={touched?.city && errors?.city}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="State *"
                      id="state"
                      name="state"
                      autoComplete="off"
                      variant="outlined"
                      {...getFieldProps('state')}
                      error={Boolean(touched?.state && errors?.state)}
                      helperText={touched?.state && errors?.state}
                    />
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                  <FormControl fullWidth>
                    <TextField
                      {...getFieldProps('address')}
                      error={Boolean(touched?.address && errors?.address)}
                      helperText={touched?.address && errors?.address}
                      multiline
                      rows={2}
                      maxRows={4}
                      label="Address *"
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <LoadingButton
                  loading={AddBranchFormik.isSubmitting}
                  sx={{ mt: 2, height: '40px', borderRadius: 2 }}
                  variant="contained"
                  type="submit"
                  size="large"
                >
                  {branchSelectedForUpdate?.id ? 'Update' : 'Create'}
                </LoadingButton>
              </Box>
            </Box>
          </Form>
        </FormikProvider>
      </Modal>

      <SnackBarAlert
        open={isSnackBarAlertOpen}
        title={snackBarMetas?.title}
        message={snackBarMetas?.message}
        severity={snackBarMetas?.severity}
        handleClose={() => setIsSnackBarAlertOpen(false)}
      />
    </Box>
  );
}
